/**
 * Leitwarte (24.09.2026): Nicos Master-Dashboard, eine dunkle Seite ohne den
 * LMS-Rahmen. Reiter Übersicht · Daten · Live · Vorgänge · Zusagen, Reiterwahl in der
 * URL (?tab=, optional &id= für einen Vorgang). Nur für Admins; alle anderen
 * landen bei ihren Anfragen.
 */

import { useEffect, useState, type FormEvent } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useAuthStore } from '@/hooks/useAuth';
import { getUebersicht, sendeNotiz } from '@/lib/leitwarte-api';
import Uebersicht, { type LeitwarteTab } from './Uebersicht';
import Daten from './Daten';
import Live from './Live';
import Vorgaenge from './Vorgaenge';
import Zusagen from './Zusagen';
import { datumLang, uhrzeit, T } from './bausteine';
import './leitwarte.css';

// Vorgänge zuerst (Nico, 24.09.2026): Morgens zählt, was entschieden werden
// muss; die Übersicht ist der Blick daneben.
const TABS: Array<{ id: LeitwarteTab; label: string }> = [
  { id: 'vorgaenge', label: 'Vorgänge' },
  { id: 'uebersicht', label: 'Übersicht' },
  { id: 'daten', label: 'Daten' },
  { id: 'live', label: 'Live' },
  { id: 'zusagen', label: 'Zusagen' },
];
const istTab = (s: string | null): s is LeitwarteTab => TABS.some((t) => t.id === s);

export default function LeitwartePage() {
  const { user } = useAuthStore();
  const istAdmin = user?.role === 'admin';
  const [params, setParams] = useSearchParams();
  const tabParam = params.get('tab');
  const tab: LeitwarteTab = istTab(tabParam) ? tabParam : 'vorgaenge';
  const fokusId = params.get('id');
  const qc = useQueryClient();

  const uebersicht = useQuery({ queryKey: ['leitwarte', 'uebersicht'], queryFn: getUebersicht, enabled: istAdmin, refetchInterval: 5 * 60 * 1000 });

  const [notiz, setNotiz] = useState('');
  const notizMut = useMutation({
    mutationFn: (text: string) => sendeNotiz(text, 'web'),
    onSuccess: () => {
      toast.success('Notiz gespeichert, wird zugeordnet');
      setNotiz('');
      qc.invalidateQueries({ queryKey: ['leitwarte', 'notizen'] });
    },
    onError: (e: Error) => toast.error(e.message || 'Notiz konnte nicht gespeichert werden'),
  });

  // Seitentitel und Body-Hintergrund für die Dauer der Seite, danach zurück.
  useEffect(() => {
    const titel = document.title;
    const hintergrund = document.body.style.background;
    document.title = 'Leitwarte';
    document.body.style.background = T.seite;
    return () => {
      document.title = titel;
      document.body.style.background = hintergrund;
    };
  }, []);

  if (!istAdmin) return <Navigate to="/leads" replace />;

  const wechsle = (ziel: LeitwarteTab, id?: string) => {
    const p = new URLSearchParams();
    if (ziel !== 'vorgaenge') p.set('tab', ziel);
    if (id) p.set('id', id);
    setParams(p);
    window.scrollTo({ top: 0 });
  };

  const notizAbsenden = (e: FormEvent) => {
    e.preventDefault();
    const text = notiz.trim();
    if (!text || notizMut.isPending) return;
    notizMut.mutate(text);
  };

  const lauf = uebersicht.data?.letzter_lauf ?? null;
  const laufFarbe = !lauf ? T.ged : lauf.status === 'ok' ? T.gut : lauf.status === 'fehler' ? T.krit : T.warn;
  const laufText = lauf
    ? `Nachtlauf ${uhrzeit(lauf.beendet_at ?? lauf.gestartet_at)}${lauf.zusammenfassung ? ` · ${lauf.zusammenfassung}` : lauf.status === 'fehler' ? ' · Fehler' : lauf.status === 'laeuft' ? ' · läuft' : ''}`
    : uebersicht.isLoading ? 'Lauf wird gelesen …' : 'noch kein Lauf';

  return (
    <div className="lw">
      <header className="lw-kopfzeile">
        <span className="lw-marke">LEITWARTE</span>
        <span className="lw-sek" style={{ fontSize: 13 }}>{datumLang(new Date())}</span>
        <span className="lw-status" title={lauf?.fehler ?? (lauf ? `${lauf.name}, ${lauf.status}` : undefined)}>
          <span className="lw-punkt" style={{ background: laufFarbe }} />
          {laufText}
        </span>

        <form className="lw-notiz" onSubmit={notizAbsenden}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.blau} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0 }}>
            <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
            <path d="M19 10v2a7 7 0 0 1-14 0v-2M12 19v3" />
          </svg>
          <input type="text" placeholder="Notiz sprechen oder tippen" value={notiz} onChange={(e) => setNotiz(e.target.value)} aria-label="Notiz" maxLength={2000} />
          <button type="submit" className="lw-knopf lw-knopf-primaer lw-knopf-klein" disabled={!notiz.trim() || notizMut.isPending}>
            {notizMut.isPending ? 'Speichert …' : 'Aufnehmen'}
          </button>
        </form>

        <nav className="lw-tabs" aria-label="Reiter">
          {TABS.map((t) => (
            <button key={t.id} type="button" className={`lw-tab${tab === t.id ? ' lw-tab-aktiv' : ''}`} onClick={() => wechsle(t.id)} aria-current={tab === t.id ? 'page' : undefined}>
              {t.label}
            </button>
          ))}
        </nav>
        <Link to="/heute" className="lw-12" style={{ color: T.ged, whiteSpace: 'nowrap' }}>Zum LMS</Link>
      </header>

      {tab === 'uebersicht' ? (
        <Uebersicht daten={uebersicht.data} laedt={uebersicht.isLoading} fehler={uebersicht.error} nochmal={() => uebersicht.refetch()} oeffneTab={wechsle} />
      ) : null}
      {tab === 'daten' ? <Daten /> : null}
      {tab === 'live' ? <Live /> : null}
      {tab === 'vorgaenge' ? <Vorgaenge fokusId={fokusId} /> : null}
      {tab === 'zusagen' ? <Zusagen kunden={uebersicht.data?.kunden ?? []} /> : null}
    </div>
  );
}
